// Enums and lookup objects for the Dota lobby values the API exposes.

export enum GameMode {
    AllPick = 1,
    CaptainsMode = 2,
    RandomDraft = 3,
    SingleDraft = 4,
    AllRandom = 5,
    ReverseCaptainsMode = 8,
    MidOnly = 11,
    LeastPlayed = 12,
    CaptainsDraft = 16,
    AbilityDraft = 18,
    AllRandomDeathmatch = 20,
    Solo1V1Mid = 21,
    AllDraft = 22,
    Turbo = 23,
}

export const GameModeNames: Record<number, string> = {
    [GameMode.AllPick]: 'All Pick',
    [GameMode.CaptainsMode]: 'Captains Mode',
    [GameMode.AllDraft]: 'All Draft (ranked All Pick)',
    [GameMode.CaptainsDraft]: 'Captains Draft',
    [GameMode.RandomDraft]: 'Random Draft',
    [GameMode.SingleDraft]: 'Single Draft',
    [GameMode.AllRandom]: 'All Random',
    [GameMode.AllRandomDeathmatch]: 'All Random Deathmatch',
    [GameMode.AbilityDraft]: 'Ability Draft',
    [GameMode.ReverseCaptainsMode]: 'Reverse Captains Mode',
    [GameMode.MidOnly]: 'Mid Only',
    [GameMode.LeastPlayed]: 'Least Played',
    [GameMode.Solo1V1Mid]: 'Solo 1v1 Mid',
    [GameMode.Turbo]: 'Turbo',
};

export enum ServerRegion {
    USWest = 1,
    USEast = 2,
    EuropeWest = 3,
    Singapore = 5,
    Dubai = 6,
    Australia = 7,
    Stockholm = 8,
    Austria = 9,
    Brazil = 10,
    SouthAfrica = 11,
    Chile = 14,
    Peru = 15,
    India = 16,
    Japan = 19,
}

export const ServerRegionNames: Record<number, string> = {
    [ServerRegion.EuropeWest]: 'Europe West',
    [ServerRegion.Stockholm]: 'Stockholm',
    [ServerRegion.Austria]: 'Austria',
    [ServerRegion.USEast]: 'US East',
    [ServerRegion.USWest]: 'US West',
    [ServerRegion.Brazil]: 'Brazil',
    [ServerRegion.Chile]: 'Chile',
    [ServerRegion.Peru]: 'Peru',
    [ServerRegion.SouthAfrica]: 'South Africa',
    [ServerRegion.Dubai]: 'Dubai',
    [ServerRegion.India]: 'India',
    [ServerRegion.Singapore]: 'Singapore',
    [ServerRegion.Japan]: 'Japan',
    [ServerRegion.Australia]: 'Australia',
};

export enum DotaTvDelay {
    Delay10Seconds = 0,
    Delay2Minutes = 1,
    Delay5Minutes = 2,
    Delay15Minutes = 3
};

export const DotaTvDelayNames: Record<number, string> = {
    [DotaTvDelay.Delay10Seconds]: '10 seconds',
    [DotaTvDelay.Delay2Minutes]: '2 minutes',
    [DotaTvDelay.Delay5Minutes]: '5 minutes',
    [DotaTvDelay.Delay15Minutes]: '15 minutes',
};

/**
 * Valve's lobby series_type. There is no best-of-two value: the API runs a
 * bo2 under BestOf3 and stops after two games (see src/domain/series.ts).
 */
export enum SeriesType {
    NoSeries = 0,
    BestOf3 = 1,
    BestOf5 = 2,
}

export const SeriesTypeNames: Record<number, string> = {
    [SeriesType.NoSeries]: 'No Series',
    [SeriesType.BestOf3]: 'Best of 3',
    [SeriesType.BestOf5]: 'Best of 5',
};

/** Draft reserve-time penalty per side, as the tournament lobby UI offers it. */
export enum PenaltyLevel {
    None = 0,
    Reserve30SecondsPenalty = 1,
    Reserve70SecondsPenalty = 2,
    Reserve110SecondsPenalty = 3,
}

export const PenaltyLevelNames: Record<number, string> = {
    [PenaltyLevel.None]: 'None',
    [PenaltyLevel.Reserve30SecondsPenalty]: '-30 s reserve time',
    [PenaltyLevel.Reserve70SecondsPenalty]: '-70 s reserve time',
    [PenaltyLevel.Reserve110SecondsPenalty]: '-110 s reserve time',
};

export enum LobbyVisibility {
    Public = 0,
    Friends = 1,
    Unlisted = 2,
}

export const LobbyVisibilityNames: Record<number, string> = {
    [LobbyVisibility.Public]: 'Public',
    [LobbyVisibility.Friends]: 'Friends',
    [LobbyVisibility.Unlisted]: 'Unlisted',
};

export type LobbyVisibilityKey = 'public' | 'friends' | 'unlisted';

export const LobbyVisibilityKeys: Record<number, LobbyVisibilityKey> = {
    [LobbyVisibility.Public]: 'public',
    [LobbyVisibility.Friends]: 'friends',
    [LobbyVisibility.Unlisted]: 'unlisted',
};

export enum PauseSetting {
    Unlimited = 0,
    Limited = 1,
    Disabled = 2,
}

export const PauseSettingNames: Record<number, string> = {
    [PauseSetting.Unlimited]: 'Unlimited',
    [PauseSetting.Limited]: 'Limited',
    [PauseSetting.Disabled]: 'Disabled',
};

export enum CmPick {
    Random = 0,
    Radiant = 1,
    Dire = 2,
}

export const CmPickNames: Record<number, string> = {
    [CmPick.Random]: 'Random',
    [CmPick.Radiant]: 'Radiant',
    [CmPick.Dire]: 'Dire',
};

/** AI bot difficulty, as protocol.DOTABotDifficulty. */
export enum BotDifficulty {
    Passive = 0,
    Easy = 1,
    Medium = 2,
    Hard = 3,
    Unfair = 4,
}

export const BotDifficultyNames: Record<number, string> = {
    [BotDifficulty.Passive]: 'Passive',
    [BotDifficulty.Easy]: 'Easy',
    [BotDifficulty.Medium]: 'Medium',
    [BotDifficulty.Hard]: 'Hard',
    [BotDifficulty.Unfair]: 'Unfair',
};

export type BotDifficultyKey = 'passive' | 'easy' | 'medium' | 'hard' | 'unfair';

export const BotDifficultyKeys: Record<number, BotDifficultyKey> = {
    [BotDifficulty.Passive]: 'passive',
    [BotDifficulty.Easy]: 'easy',
    [BotDifficulty.Medium]: 'medium',
    [BotDifficulty.Hard]: 'hard',
    [BotDifficulty.Unfair]: 'unfair',
};

/** Lobby member states the API reports on active lobbies. */
export const LobbyStateNames: Record<number, string> = {
    0: 'UI',
    1: 'Ready-up',
    2: 'Server setup',
    3: 'Run',
    4: 'Post game',
    5: 'Not scheduled',
    6: 'Servers failed',
};
